import { Component, ElementRef, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  NavbarComponent,
  SelectedCustomerService,
  AuthService,
  CartService,
  NavbarConfigService,
  GungFlowService,
  GlobalSearchConfigService
} from 'gung-standard';
import { forkJoin, fromEventPattern } from 'rxjs';
import { filter, first, switchMap, takeUntil, tap } from 'rxjs';
import { TyloInlineSearchListConfigService } from '../../services/tylo-inline-search-list-config.service';
import { environment } from './../../../environments/environment';

@Component({
  selector: 'tylo-navbar',
  templateUrl: './tylo-navbar.component.html',
  styleUrls: ['./tylo-navbar.component.scss']
})
export class TyloNavbarComponent extends NavbarComponent implements OnInit {
  logo: string;
  userDigitalAssets = false;
  isAmerec = environment.projectName === 'amerec';
  isHelo = environment.projectName === 'helo';

  constructor(
    protected selectedCustomerService: SelectedCustomerService,
    protected authService: AuthService,
    protected cartService: CartService,
    protected elementRef: ElementRef,
    protected navbarConfigService: NavbarConfigService,
    protected gungFlowService: GungFlowService,
    protected router: Router,
    public configService: TyloInlineSearchListConfigService,
    protected globalSearchConfig: GlobalSearchConfigService
  ) {
    super(selectedCustomerService, authService, cartService, elementRef, navbarConfigService, gungFlowService, router, globalSearchConfig);
    this.logo = environment.logoName;
  }

  ngOnInit() {
    this.logoUrl = this.navbarConfigService.getLogoUrl();

    this.change
      .asObservable()
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap(() => {
          return this.authService.getIsAuthenticated().pipe(
            tap(authed => (this.signedIn = authed)),
            filter(authenticated => !!authenticated),
            tap(authenticated => (this.authenticated = authenticated)),
            switchMap(() =>
              forkJoin([
                this.authService.getRoles().pipe(
                  filter(roles => !!roles),
                  first()
                ),
                this.selectedCustomerService.getSelectedCustomer().pipe(
                  filter(customer => !!customer),
                  first()
                )
              ])
            )
          );
        })
      )
      .subscribe(([roles, customer]) => {
        this.selectedCustomer = customer;
        if (roles.indexOf('SALES') > -1 || roles.indexOf('ADMIN') > -1) {
          this.isSales = true;
        } else {
          this.isSales = false;
        }

        if (roles.indexOf('ANONYMOUS') < 0) {
          this.isAnonymous = false;
        }

        if (roles.indexOf('DIGITALASSETS') > -1) {
          this.userDigitalAssets = true;
        }

        this.navbarConfigService
          .getLeftMenus()
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(links => (this.leftMenus = links));

        this.navbarConfigService
          .getTopInformation()
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(links => (this.topInformation = links));

        this.navbarConfigService
          .getRightMenus()
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(links => (this.rightMenus = links));
      });

    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.change.next(true));

    this.cartService
      .getCurrentCart()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(cart => (this.cart = cart));

    this.navbarConfigService
      .showCart()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(showCart => {
        this.isCartVisible = showCart;
      });

    this.isCollapsed = true;
    this.isCustomerCollapsed = true;

    // When going to another path If top menu is extended
    // Then allways change its state unless for small screens (here the top menu is expanded/collapsed when click on it)
    this.router.events
      .pipe(
        takeUntil(this.unsubscribe),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(event => {
        if (!this.isSmallScreen) {
          this.collapseTopMenu();
        }
      });

    this.gungFlowService
      .getSelectedFlow()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(flow => {
        this.isOnlyDisplayFlow = !!flow && !!flow.displayOnly;
        this.collapseTopMenu();
      });

    // Evaluate the screen width
    // For small screen
    // the top menu will contain only one main menu expanded
    // the remaing will be kept collapsed
    const mediaQuery = matchMedia('screen and (max-width: 575px)');
    this.isSmallScreen = mediaQuery.matches;

    const mediaObserver = fromEventPattern<MediaQueryListEvent>(
      mediaQuery.addListener.bind(mediaQuery),
      mediaQuery.removeListener.bind(mediaQuery)
    );

    mediaObserver.pipe(takeUntil(this.unsubscribe)).subscribe(result => {
      this.isSmallScreen = result.matches;
    });

    this.gungFlowService
      .getSelectedFlow()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(flow => {
        if (!!flow) {
          this.currentFlowName = flow.name;
        }
      });
    this.showFlowsAsDropdown = this.navbarConfigService.showFlowsAsDropdown;
  }
}
